import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoComplete", "autoFocus", "anchorType", "ButtonContent", "buttonContentProps", "buttonSize", "buttonStyles", "buttonUse", "cache", "closeOnOutsideClick", "closeOnTargetLeave", "disabled", "dropdownContentRef", "dropdownFooter", "error", "id", "initiallyOpen", "inputRef", "isDisplayOptimized", "isOpen", "loadOptions", "menuHeight", "menuStyles", "menuWidth", "minimumSearchCount", "multi", "onChange", "onPaste", "options", "placement", "readOnly", "resetValue", "searchPlaceholder", "resetOption", "searchable", "SearchInput", "selectComponent", "selectRef", "value", "valueRenderer", "placeholder", "_forcePlaceholder", "cssStyles", "focused", "hovered", "caretRenderer"];
import { Component } from 'react';
import ReactDOM from 'react-dom';
import ShareInput from '../ShareInput/ShareInput';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { AbstractDropdownWithSearchbox } from '../AbstractDropdown/AbstractDropdownWithSearchbox';
import { TypeaheadSearch } from '../Typeahead/TypeaheadSearch';
import { Tag } from '../Tag/Tag';
import { DropdownCaret } from '../AbstractDropdown/DropdownCaret';
import { StyledDropdownResults, StyledMultiValueSpan, StyledTypeaheadResultsSpan } from './Select.styles';
import unescapedText from 'I18n/utils/unescapedText';
import { getButtonProps, getValueOption } from '../../utils/SelectOptionUtils';
import { getAriaAndDataProps } from '../../utils/Props';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MIN_MENU_WIDTH = 144;
const DISPLAY_NONE_STYLE = {
  display: 'none'
};
const noop = () => {};
const getEventProps = props => {
  const eventPropKeyRegex = /^on[A-Z]/;
  return Object.keys(props).filter(key => {
    return key !== 'onClose' && key !== 'onInputChange' && key !== 'onInputKeyDown' && key !== 'onOpen' && key !== 'onInputValueChange';
  }).reduce((acc, key) => {
    if (!key) return acc;
    if (key.match(eventPropKeyRegex)) {
      acc[key] = props[key];
    }
    return acc;
  }, {});
};
class SelectWithButtonAnchorCore extends Component {
  constructor(...args) {
    super(...args);
    this._autoloadSelectEl = null;
    this.state = {
      inputCache: {},
      inputValue: '',
      searchCount: 0
    };
    this.focus = () => {
      const {
        onOpenChange,
        isOpen
      } = this.props;
      if (!isOpen) onOpenChange(syntheticEvent(true));
    };
    this.selectRefCallback = ref => {
      const {
        selectRef
      } = this.props;

      // When react-select mounts, ask it how many (flattened) options it has
      this._selectRef = ref;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        this.setState({
          searchCount: internalSelect._flatOptions.length
        });
      }
      if (selectRef) {
        if (typeof selectRef === 'function') {
          selectRef(ref);
        } else {
          selectRef.current = ref;
        }
      }
    };
    this.getInternalSelect = () => {
      // Return our react-select component instance, or null
      if (!this._selectRef) {
        return null;
      }
      if (!('select' in this._selectRef) || !this._selectRef.select) {
        return this._selectRef;
      }
      // _selectRef must be an <Async> or <Creatable> wrapper
      return this._selectRef.select;
    };
    this.getInternalAsyncSelect = () => {
      // Return our react-select Async instance, or null
      if (!this._selectRef || !('select' in this._selectRef) || !this._selectRef.select) {
        return null;
      }
      return this._selectRef;
    };
    this.handleInputKeyDown = evt => {
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleKeyDown(evt);
        setTimeout(() => {
          this.forceUpdate(); // Ensure that the input renders with updated ARIA attrs on up/down
        });
      }
    };
    this.handleInputChange = evt => {
      const {
        onInputValueChange
      } = this.props;
      callIfPossible(onInputValueChange, evt);
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleInputChange(evt);
      }
      // This setState() forces an update, ensuring that Tether is repositioned properly.
      this.setState({
        inputValue: evt.target.value
      });
    };
    this.handleInputFocus = () => {
      // React Select thinks it's closed when its input receives a blur event.
      // We don't want that, because it ignores keydown events when closed.
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.setState({
          isOpen: true
        });
      }
    };
    this.handleFocusLeave = () => {
      // Temporarily disabled. This causes close-and-reopen when clicking the anchor.
      // const { onOpenChange } = this.props;
      // onOpenChange(SyntheticEvent(false));
      // }
    };
    this.handleRemoveValueClick = (optionValue, evt) => {
      const {
        onChange,
        value
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        const internalSelectValueArray = internalSelect.getValueArray(internalSelect.props.value);
        internalSelect.setValue(internalSelectValueArray.filter(option => option.value !== optionValue));
      } else {
        // Select is unmounted (e.g. because the dropdown is closed), so we handle the change
        // ourselves. Note that the "value" prop on SelectWithButtonAnchor is an array of options.
        const allOptions = (value || []).map(v => {
          return typeof v === 'object' ? v : this.getOptionForSingleValue(v);
        });
        const newOptions = allOptions.filter(o => o.value !== optionValue);
        onChange(newOptions);
      }
      evt.stopPropagation(); // Prevent dropdown toggle
    };
    this.hasSearchbox = ({
      loadOptions,
      minimumSearchCount,
      searchable
    }) => {
      // No search, no searchbox.
      if (!searchable) {
        return false;
      }
      // If we're in async mode, always show the searchbox. Otherwise, show it only if the number of
      // (flattened) options meets minimumSearchCount, or if a search has been entered.
      return !!loadOptions || this.state.searchCount >= minimumSearchCount || Boolean(this.state.inputValue);
    };
    this.getOptionForSingleValue = singleValue => {
      const {
        options
      } = this.props;

      // Check if value is an Option
      if (singleValue && typeof singleValue === 'object' && 'value' in singleValue) {
        return singleValue;
      }

      // Check if value maps to an existing option
      const option = getValueOption(options, singleValue);
      if (option) {
        return option;
      }

      // Create new option from primitive value
      return {
        text: String(singleValue),
        value: singleValue
      };
    };
    this.getButtonContent = ({
      disabled,
      readOnly,
      loadOptions,
      multi,
      options,
      placeholder,
      resetOption,
      resetValue,
      value,
      valueRenderer,
      _forcePlaceholder
    }) => {
      if (_forcePlaceholder) {
        return {
          valueIsValid: true,
          buttonText: placeholder
        };
      }
      if (multi) {
        const renderedMultiValues = this.renderMultiValues({
          disabled,
          readOnly,
          placeholder,
          value,
          valueRenderer
        });
        return {
          valueIsValid: true,
          buttonText: /*#__PURE__*/_jsx(StyledMultiValueSpan, {
            "data-fnd-select-multi-value": "t",
            $hasValueTags: Array.isArray(renderedMultiValues),
            children: renderedMultiValues
          })
        };
      }
      return getButtonProps({
        async: !!loadOptions,
        options,
        placeholder: placeholder || '',
        resetOption,
        resetValue,
        value: multi ? value : value,
        valueRenderer,
        multi: multi
      });
    };
    this.renderMultiValues = ({
      disabled,
      readOnly,
      placeholder,
      value,
      valueRenderer
    }) => {
      if (!Array.isArray(value) || value.length === 0) {
        return placeholder;
      }
      return value.map(v => {
        const option = this.getOptionForSingleValue(v);
        const handleCloseClick = this.handleRemoveValueClick.bind(this, option.value);
        return /*#__PURE__*/_jsx(Tag, {
          closeable: !disabled && !readOnly && option.clearableValue !== false,
          onCloseClick: handleCloseClick,
          use: option.tagUse || 'default',
          children: valueRenderer(option)
        }, `${option.value}`);
      });
    };
    this.renderSearch = ({
      autoComplete,
      initiallyOpen,
      inputRef,
      onPaste,
      SearchInput,
      searchPlaceholder
    }, {
      hasSearchbox
    }) => {
      let ariaActiveDescendant;
      let ariaOwns;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        const valueArray = internalSelect.getValueArray(internalSelect.props.value);
        ariaActiveDescendant = `${internalSelect._instancePrefix}-option-${internalSelect.getFocusableOptionIndex(valueArray[0])}`;
        ariaOwns = `${internalSelect._instancePrefix}--list`;
      }

      // TODO: this should be abstracted but we need to figure out a way to
      // handle ScreenReaderOnly styles in a way that doesn't cause children
      // to be conditionally rendered as it's causing bugs
      const screenReaderProps = hasSearchbox ? {} : {
        position: 'absolute',
        insetBlockStart: '0',
        inlineSize: '1px',
        blockSize: '1px',
        margin: '-25',
        padding: '0',
        overflow: 'hidden',
        borderWidth: '0',
        clipPath: 'inset(50%)'
      };
      return /*#__PURE__*/_jsx(TypeaheadSearch, Object.assign({
        "aria-activedescendant": ariaActiveDescendant,
        "aria-owns": ariaOwns,
        autoComplete: autoComplete,
        autoFocus: !initiallyOpen,
        Input: SearchInput,
        inputRef: inputRef,
        onKeyDown: this.handleInputKeyDown,
        onChange: this.handleInputChange,
        onFocus: this.handleInputFocus,
        onPaste: onPaste,
        placeholder: searchPlaceholder,
        role: "combobox"
      }, screenReaderProps));
    };
  }
  componentDidMount() {
    const {
      autoload,
      cache,
      isOpen,
      loadOptions,
      selectComponent
    } = this.props;

    // Mount a separate Select.Async instance to perform autoload, if needed. This is a weird hack,
    // but a necessary one since the normal Select isn't mounted until the user opens the dropdown.
    if (autoload && loadOptions && !isOpen) {
      this._autoloadSelectEl = document.createElement('div');
      const SelectComponent = selectComponent;
      ReactDOM.render( /*#__PURE__*/_jsx(SelectComponent, {
        autoload: autoload,
        cache: typeof cache === 'undefined' ? this.state.inputCache : cache,
        loadOptions: loadOptions
      }), this._autoloadSelectEl);
    }
  }
  componentWillUnmount() {
    if (this._autoloadSelectEl) {
      ReactDOM.unmountComponentAtNode(this._autoloadSelectEl);
      this._autoloadSelectEl = null;
    }
  }
  render() {
    const _this$props = this.props,
      {
        autoComplete,
        autoFocus,
        anchorType,
        ButtonContent,
        buttonContentProps,
        buttonSize = 'medium',
        buttonStyles,
        buttonUse = 'secondary',
        cache,
        closeOnOutsideClick = true,
        closeOnTargetLeave,
        disabled,
        dropdownContentRef,
        dropdownFooter,
        error,
        id,
        initiallyOpen,
        inputRef,
        isDisplayOptimized,
        isOpen,
        loadOptions,
        menuHeight,
        menuStyles,
        menuWidth,
        minimumSearchCount = 8,
        multi = false,
        onChange,
        onPaste,
        options,
        placement,
        readOnly,
        resetValue,
        searchPlaceholder = unescapedText('salesUI.UISearchableSelectInput.placeholder'),
        resetOption,
        searchable = true,
        SearchInput,
        selectComponent,
        value,
        valueRenderer,
        placeholder,
        _forcePlaceholder = false,
        cssStyles,
        focused,
        hovered,
        caretRenderer = /*#__PURE__*/_jsx(DropdownCaret, {
          buttonUse: buttonUse,
          buttonSize: buttonSize,
          hidden: isDisplayOptimized && !(focused || hovered)
        })
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      buttonText,
      valueIsValid
    } = this.getButtonContent({
      disabled,
      readOnly,
      loadOptions,
      multi,
      options,
      placeholder,
      resetOption,
      resetValue,
      value,
      valueRenderer,
      _forcePlaceholder
    });
    const hasSearchbox = this.hasSearchbox({
      loadOptions,
      minimumSearchCount,
      searchable
    });
    const SelectComponent = selectComponent;
    return /*#__PURE__*/_jsxs(AbstractDropdownWithSearchbox, Object.assign({}, getAriaAndDataProps(rest), getEventProps(rest), {
      _useNativeButton: false,
      "aria-invalid": error != null ? error : !valueIsValid,
      autoFocus: autoFocus,
      buttonText: ButtonContent == null ? buttonText : /*#__PURE__*/_jsx(ButtonContent, Object.assign({
        buttonText: buttonText
      }, buttonContentProps)),
      buttonSize: buttonSize,
      buttonUse: buttonUse,
      cssStyles: cssStyles,
      caretRenderer: caretRenderer,
      closeOnOutsideClick: closeOnOutsideClick,
      closeOnTargetLeave: closeOnTargetLeave,
      disabled: disabled,
      dropdownContentRef: dropdownContentRef,
      hasSearchbox: hasSearchbox,
      id: id,
      isDisplayOptimized: isDisplayOptimized,
      menuWidth: menuWidth,
      multiline: multi,
      onFocusLeave: this.handleFocusLeave,
      open: isOpen,
      placement: placement,
      readOnly: readOnly,
      buttonStyles: buttonStyles,
      "data-has-custom-button-content": String(!!ButtonContent),
      "data-fnd-select-multi-value": multi ? 't' : undefined,
      children: [this.renderSearch({
        autoComplete,
        initiallyOpen,
        inputRef,
        onPaste,
        SearchInput,
        searchPlaceholder
      }, {
        hasSearchbox
      }), /*#__PURE__*/_jsx(StyledTypeaheadResultsSpan, {
        "data-dropdown-results": true,
        "data-async-options": !!loadOptions,
        "data-test-id": "typeahead-results",
        children: /*#__PURE__*/_jsx(StyledDropdownResults, {
          $searchable: hasSearchbox,
          $menuHeight: menuHeight,
          $menuStyles: menuStyles,
          $anchorType: anchorType,
          children: /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, rest, {
            autosize: false,
            cache: typeof cache === 'undefined' ? this.state.inputCache : cache,
            disabled: disabled,
            isOpen: true,
            loadOptions: loadOptions,
            multi: multi,
            onChange: onChange,
            options: options,
            readOnly: readOnly,
            ref: this.selectRefCallback,
            resetValue: resetValue,
            scrollMenuIntoView: true,
            style: DISPLAY_NONE_STYLE,
            tabIndex: "-1",
            value: value,
            valueRenderer: noop
          }))
        })
      }), dropdownFooter]
    }));
  }
}
export const SelectWithButtonAnchor = ShareInput(SelectWithButtonAnchorCore);